import React from "react";
import { Helmet } from "react-helmet";
import { useStaticQuery, graphql } from "gatsby";
import usePageContext from "context/PageContext";
import { SUPPORTED_LANGUAGES } from "utils/languageConfig.mjs";

const DocumentHead = () => {
  const { metaData } = useStaticQuery(query);
  const { lang, unlocalizedSlug, pageTitle } = usePageContext();

  const host = "https://www.formfeld.net";
  // Pfad ohne Sprache, immer mit fuehrendem und abschliessendem Schraegstrich
  const trimmed = (unlocalizedSlug || "").replace(/^\/+|\/+$/g, "");
  const slug = trimmed ? `/${trimmed}/` : "/";
  // Startseite bekommt einen eigenen Titel je Sprache (Suchbegriff fuer Google).
  const homeTitles = {
    de: "Formfeld – Mathematisch geformte Akustikpaneele",
    en: "Formfeld – Mathematically shaped acoustic panels",
  };
  // Feste Seiten ohne eigenen Sanity-Titel
  const staticTitles = {
    "/shop/": { de: "Shop", en: "Shop" },
    "/projects/": { de: "Projekte", en: "Projects" },
    "/journal/": { de: "Journal", en: "Journal" },
    "/rawMaterials/": { de: "Material", en: "Material" },
    "/cart/": { de: "Warenkorb", en: "Cart" },
  };
  // Unterseiten: "Seitenname – Formfeld", Name aus Sanity bzw. Shopify
  const subTitle =
    (pageTitle && (pageTitle[lang] || pageTitle.de)) ||
    (staticTitles[slug] && staticTitles[slug][lang]);
  // Beschreibung fuer Google: DE kommt aus Sanity, EN hier fest hinterlegt.
  const descriptions = {
    en: "Formfeld develops mathematically shaped wall panels made of natural materials. Their relief improves interior acoustics. Made in Germany.",
  };
  const description = descriptions[lang] || metaData.description;
  // Strukturierte Daten (schema.org) fuer Google und KI-Suchen: Firma Formfeld.
  // Bewusst ohne Adresse, damit niemand ein Ladengeschaeft erwartet.
  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${host}/#organization`,
    name: "Formfeld",
    url: `${host}/`,
    logo: `${host}/formfeld-logo.png`,
    email: "info@formfeld.net",
    description,
    sameAs: ["https://www.instagram.com/formfeld/"],
  };
  const title =
    unlocalizedSlug === "/" && homeTitles[lang]
      ? homeTitles[lang]
      : subTitle
        ? `${subTitle} – ${metaData.title}`
        : metaData.title;
  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
    >
      <title>{title}</title>
      <link rel="canonical" href={`${host}/${lang}${slug}`} />
      <link rel="alternate" hrefLang="x-default" href={`${host}/de${slug}`} />
      {SUPPORTED_LANGUAGES.map((supportedLang) => (
        <link
          key={supportedLang.code}
          rel="alternate"
          hrefLang={supportedLang.code}
          href={`${host}/${supportedLang.code}${slug}`}
        />
      ))}
      <meta name="description" content={description} />

      {/*FAVICON*/}

      <meta rel="icon" href={metaData.favicon.asset.url} />
      <link rel="icon" href={metaData.favicon.asset.url} />

      {/* SOCIAL MEDIA: Open Graph Tags */}
      {title && <meta property="og:title" content={title} />}
      {description && (
        <meta property="og:description" content={description} />
      )}
      <meta property="og:locale" content={lang} />

      {/* STRUKTURIERTE DATEN */}
      <script type="application/ld+json">
        {JSON.stringify(organization)}
      </script>
    </Helmet>
  );
};

export default DocumentHead;

const query = graphql`
  query SeoQuery {
    metaData: sanitySiteSettings {
      description
      title
      favicon {
        asset {
          url
        }
      }
    }
  }
`;
